import { AsyncPanel } from '../ui/AsyncPanel'
import type { ResourceState } from '../data/resource'
import type { PublicListing } from '../data/endpoints'
import { CustomerShell, rupees } from './CustomerShell'

/**
 * One listing, and the only screen with an "Add to cart" on it.
 *
 * The add control is the footer's primary action, low and right, and it carries the
 * attention halo because it is unambiguously the next thing. A sold-out listing
 * disables it and says why in words next to it, rather than leaving a dead button
 * for a customer to tap twice and assume the site is broken.
 *
 * The caption is the one Mary wrote at the catalogue station. It is rendered as
 * plain text, not HTML: it comes from a text field a human types into and nothing
 * downstream of that field should be able to inject markup.
 */
export interface ListingDetailProps {
  state: ResourceState<PublicListing>
  creditPaise: number | null
  cart: { lines: number; payablePaise: number } | null
  photoUrl: (r2Key: string) => string
  adding?: boolean
  onAdd?: (listingId: string) => void
  onRetry?: () => void
  online?: boolean
}

export function ListingDetail({
  state,
  creditPaise,
  cart,
  photoUrl,
  adding = false,
  onAdd,
  onRetry,
  online,
}: ListingDetailProps) {
  const listing = state.status === 'ready' ? state.data : null
  const canAdd = !!listing && !listing.soldOut && !adding

  return (
    <CustomerShell
      title={listing?.name ?? 'Listing'}
      creditPaise={creditPaise}
      cart={cart}
      online={online}
      primaryAction={
        listing
          ? {
              label: adding ? 'Adding...' : listing.soldOut ? 'Sold' : 'Add to cart',
              disabled: !canAdd,
              onPress: () => onAdd?.(listing.id),
            }
          : undefined
      }
    >
      <AsyncPanel state={state} emptyMessage="This listing is gone" onRetry={onRetry}>
        {(l) => (
          <article className="sf-detail">
            {l.photoR2Key ? (
              <img className="sf-photo sf-photo-large" src={photoUrl(l.photoR2Key)} alt={l.name} />
            ) : null}
            <h2 className="t-title">{l.name}</h2>
            <p className="sf-price t-display">{rupees(l.pricePaise)}</p>
            {l.soldOut ? <p className="sf-sold-label">Sold - someone got there first</p> : null}
            {l.captionMd ? <p className="sf-caption">{l.captionMd}</p> : null}
            <p className="muted t-label">{l.qtyAvailable} available</p>
          </article>
        )}
      </AsyncPanel>
    </CustomerShell>
  )
}
